import { useEffect, useMemo, useState } from "react";
import Footer from "../../components/layouts/Footer";
import ArtworkCard from "../../components/ArtworkCard";
import AboutSeller from "../../components/AboutSeller";
import SellerReviews from "../../components/SellerReviews";
import { Link, useParams } from "react-router-dom";
import { usePersistStore, useStore } from "../../store";
import { ArtworkCardProps } from "../../types";
import { AD_TYPES } from "../../utils/enums";
import { Skeleton } from "boneyard-js/react";
import {
  useSellerProfile,
  useSellerAggregate,
  useSellerAds,
} from "../../hooks/queries";

function Details() {
  const { id } = useParams();
  const { currentLocation } = usePersistStore((s: any) => s);
  const { setErrorMessage } = useStore((s: any) => s);
  const sellerId = id || undefined;
  const [type, setType] = useState(AD_TYPES.OPEN_FOR_BIDS);

  const { data: profile, error: profileError } = useSellerProfile(
    sellerId,
    currentLocation
  );
  const seller = profile?.seller ?? null;
  const { data: aggregate = null } = useSellerAggregate(sellerId);
  // The type-filtered artwork list is its own query (keyed on type) so the
  // tab filter and the profile fetch never race.
  const {
    data: adsData = [],
    isLoading,
    error: adsError,
  } = useSellerAds(sellerId, type, currentLocation);
  const isReady = !isLoading;

  useEffect(() => {
    const e = profileError || adsError;
    if (e) setErrorMessage((e as Error).message);
  }, [profileError, adsError, setErrorMessage]);

  const list: ArtworkCardProps[] = useMemo(
    () =>
      (adsData || []).map((tt: any) => ({
        _id: tt?._id,
        color: true,
        imageUrl: tt?.images?.[0],
        category: tt?.categoryId?.name,
        subCategory: tt?.subCategoryId?.name,
        views: tt?.views || 0,
        title: tt?.title,
        status: tt?.status,
        ownerImage: tt?.userId?.fileUrl,
        ownerName: tt?.userId?.name,
        ownerId: tt?.userId?._id,
        biddingStarts: tt?.biddingStarts,
        biddingEnds: tt?.biddingEnds,
        price: tt?.price,
        showBidNowButton: type === AD_TYPES.OPEN_FOR_BIDS,
        distance: tt?.distance,
      })),
    [adsData, type]
  );

  const filters: { type: string; label: string }[] = [
    { type: AD_TYPES.OPEN_FOR_BIDS, label: "Open for bids" },
    { type: AD_TYPES.UPCOMING, label: "Upcoming" },
    { type: AD_TYPES.SOLD, label: "Sold" },
  ];

  return (
    <>
      <div className="container px-md-0 px-3">
        <div className="sellerProfile">
          <div className="breadCrumb mt-4 mb-3">
            <div className="d-flex gap-3">
              <Link to="/">
                <p className="m-0">Home</p>
              </Link>
              <span>/</span>
              <Link to="/sellers">
                <p className="m-0">Our artists</p>
              </Link>
              <span>/</span>
              <h4 className="mb-0">{seller?.name || "Seller profile"}</h4>
            </div>
          </div>

          <Skeleton name="about-seller" loading={!seller}>
            {seller ? (
              <AboutSeller seller={seller} aggregate={aggregate} />
            ) : (
              // Aspect-ratio placeholder so boneyard's absolute-positioned
              // bones have a sized container to overlay while the seller
              // is loading. Once the real seller arrives we drop this
              // wrapper entirely — leaving it in place would impose a
              // tall reserved height on sparse profiles (no rating, no
              // sales, no description) and leave a visible empty band
              // between the card and the Artworks section.
              <div className="aboutSellerSkeletonWrap" />
            )}
          </Skeleton>

          <div className="sellerArtworks">
            <div className="sellerArtworks__toolbar">
              <h2 className="sellerArtworks__title">Artworks</h2>
              <div className="sellerArtworks__filters" role="tablist">
                {filters.map((f) => (
                  <button
                    key={f.type}
                    type="button"
                    role="tab"
                    aria-selected={type === f.type}
                    className={`sellerArtworks__filter ${
                      type === f.type ? "sellerArtworks__filter--active" : ""
                    }`}
                    onClick={() => setType(f.type)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="row">
              {!isReady &&
                Array.from({ length: 3 }).map((_, i) => (
                  <div
                    className="col-12 col-lg-6 col-xl-4 mb-4"
                    key={`sk-${i}`}
                  >
                    <Skeleton name="artwork-card" loading={true}>
                      <span style={{ display: "block", aspectRatio: "3 / 4" }} />
                    </Skeleton>
                  </div>
                ))}
              {isReady && list?.length === 0 && (
                <div className="col-12">
                  <div className="sellerArtworks__empty">
                    <i
                      className="bi bi-easel sellerArtworks__emptyIcon"
                      aria-hidden="true"
                    />
                    <div className="sellerArtworks__emptyTitle">
                      Nothing here yet
                    </div>
                    <div className="sellerArtworks__emptyText">
                      No artworks in this category at the moment.
                    </div>
                  </div>
                </div>
              )}
              {isReady &&
                list?.map((item: ArtworkCardProps, index) => (
                  <div
                    className="col-12 col-lg-6 col-xl-4 mb-4"
                    key={index}
                  >
                    <ArtworkCard {...item} />
                  </div>
                ))}
            </div>
          </div>

          {sellerId && <SellerReviews sellerId={sellerId as string} />}
        </div>
      </div>

      <Footer />
    </>
  );
}

export default Details;
